import { Suspense, lazy, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// project imports
import useAuth from 'hooks/useAuth';
import { useDispatch, useSelector } from 'store';
import { datLai, getDangKyLuuTruHienTai, moDialog } from 'store/slices/dangKyLuuTru';
import DangKyLuuTruErrorBoundary from './DangKyLuuTruErrorBoundary';
import { TAM_AN_LUU_TRU } from './tamAn';

// Tách khỏi bundle khởi động: người không cần tới thì không tải.
const DangKyLuuTruDialog = lazy(() => import('./DangKyLuuTruDialog'));

// Danh sách TRẮNG các vai trò được đăng ký. Dùng danh sách trắng thay vì danh sách đen vì
// FirebaseContext có nhánh cho ra role === undefined (tài khoản isSupport không có mã hạnh sinh),
// giá trị đó lọt qua danh sách đen. Vai trò ngoài danh sách này: gate thoát ngay —
// không gọi API, không render gì thêm — hệ thống chạy y hệt như trước khi có tính năng.
const VAI_TRO_DUOC_DANG_KY = ['HANHSINH', 'HSDD'];

// Trang đăng ký có sẵn form y hệt dialog: đứng ở đó thì không tự mở popup đè lên.
const DUONG_DAN_TRANG_DANG_KY = '/hanh-sinh-dang-ky-luu-tru';

// Bỏ dấu `/` thừa ở cuối trước khi so đường dẫn: người dùng gõ tay
// '/hanh-sinh-dang-ky-luu-tru/' vẫn phải được coi là đang đứng ở trang đăng ký.
const chuanHoaDuongDan = (duongDan) => String(duongDan || '').replace(/\/+$/, '');

// Nguồn sự thật DUY NHẤT cho câu hỏi "tài khoản này được đăng ký không?".
// Gate dùng để quyết định có chạy gì không; trang đăng ký dùng để giải thích cho người
// bị loại, thay vì treo "Đang tải…" vĩnh viễn do không ai gọi API hộ họ.
export const duocDangKy = (user) => Boolean(user) && Boolean(user.maHs) && VAI_TRO_DUOC_DANG_KY.includes(user.role);

// Toàn bộ logic tính năng nằm ở đây, và component này luôn được render BÊN TRONG
// ErrorBoundary. Boundary là con thì không bắt được lỗi do cha ném ra, nên mọi hook
// có thể ném (selector, effect) phải nằm dưới boundary một tầng.
const DangKyLuuTruGateHanhSinh = ({ maHs }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const daTuMo = useRef(false);

    const { dot, dangKy, canEdit } = useSelector((state) => state.dangKyLuuTru);

    // Phụ thuộc maHs, KHÔNG chốt theo `loaded`: đổi tài khoản trong cùng tab thì nạp lại đúng
    // dữ liệu người mới, và một lần lỗi mạng không dính cả phiên. MainLayout không remount khi
    // chuyển route con nên đây vẫn là một request mỗi phiên đăng nhập.
    useEffect(() => {
        // Xoá sạch dữ liệu người trước TRƯỚC khi tải dữ liệu người mới: giữa lúc đăng nhập và
        // lúc API trả về (mạng yếu, cộng getIdToken) có thể vài giây — không ai được nhìn thấy
        // đăng ký của người khác dù chỉ một nhịp.
        dispatch(datLai());
        dispatch(getDangKyLuuTruHienTai());
    }, [dispatch, maHs]);

    // Nhắc MỖI LẦN mở app cho tới khi đăng ký xong — cố ý không ghi nhớ "Để sau" ở trình
    // duyệt. Đăng ký rồi thì `dangKy` khác null nên không bao giờ nhắc nữa.
    // `daTuMo` chỉ chặn việc bật lại ngay trong cùng một phiên: đóng popup rồi đi lại giữa
    // các trang thì không bị bật lên lần nữa, phải tải lại app mới nhắc tiếp.
    useEffect(() => {
        if (!dot || daTuMo.current) return;
        if (!canEdit || dangKy) return;
        if (chuanHoaDuongDan(location.pathname) === DUONG_DAN_TRANG_DANG_KY) return;

        daTuMo.current = true;
        dispatch(moDialog());
    }, [dispatch, dot, dangKy, canEdit, location.pathname]);

    // Đợt đang tắt thì Dialog chỉ return null — đừng tải chunk cho hàng trăm hạnh sinh.
    return dot ? (
        <Suspense fallback={null}>
            <DangKyLuuTruDialog />
        </Suspense>
    ) : null;
};

const DangKyLuuTruGate = () => {
    const { user } = useAuth();

    // Tài khoản không gắn mã hạnh sinh thì không nhắc: có điền cũng bị backend trả 400.
    if (TAM_AN_LUU_TRU || !duocDangKy(user)) return null;

    return (
        <DangKyLuuTruErrorBoundary>
            <DangKyLuuTruGateHanhSinh maHs={user.maHs} />
        </DangKyLuuTruErrorBoundary>
    );
};

export default DangKyLuuTruGate;
